import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { servicesContent } from '../data/servicesContent';

const scrollToContact = (service) => {
  if (service) window.dispatchEvent(new CustomEvent('soulboard:apply', { detail: { service } }));
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
};

const PersonalSoulboardSection = () => {
  const { language } = useLanguage();
  const c = servicesContent[language];

  return (
    <section id="personal-soulboard" className="py-24 lg:py-28 bg-[#faf7ee]/40 overflow-hidden" data-testid="personal-soulboard-section">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* LEFT: text */}
          <motion.div
            className="space-y-5"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-[11px] tracking-[0.4em] uppercase text-neutral-500">{c.psEyebrow}</p>
            <h2
              className="offering-title leading-[0.95] text-neutral-900"
              style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(2rem, 4.2vw, 3.25rem)' }}
            >
              {c.psTitle}
            </h2>
            <p className="text-[11px] tracking-[0.35em] uppercase" style={{ color: '#7d65b8' }}>
              {c.psSubhead}
            </p>
            <div className="w-12 h-[2px] bg-[#CCFF00]" />

            <div className="space-y-3">
              {c.psCopy.map((p, i) => (
                <p key={i} className={`${i === 0 ? 'text-base lg:text-lg text-neutral-800' : 'text-sm lg:text-base text-neutral-700'} leading-relaxed`}>
                  {p}
                </p>
              ))}
            </div>

            {c.psDetails.length > 0 && (
              <div className="pt-1">
                {c.psDetailsLabel && (
                  <p className="text-[10px] tracking-[0.35em] uppercase mb-2" style={{ color: '#7d65b8' }}>
                    {c.psDetailsLabel}
                  </p>
                )}
                <ul className="space-y-1.5">
                  {c.psDetails.map((d) => (
                    <li key={d} className="text-sm lg:text-base text-neutral-800 flex items-start gap-2">
                      <span style={{ color: '#CCFF00' }} className="mt-0.5 font-bold">·</span>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-baseline gap-3 flex-wrap pt-1">
              <p className="text-3xl lg:text-4xl text-neutral-900" style={{ fontWeight: 500 }} data-testid="personal-soulboard-price">
                {c.psPrice}
              </p>
            </div>

            <div className="pt-2">
              <motion.button
                onClick={() => scrollToContact('personal-soulboard')}
                className="px-7 py-3.5 text-sm uppercase tracking-[0.25em] font-bold"
                style={{ background: '#CCFF00', color: '#171717' }}
                whileHover={{ scale: 1.03, boxShadow: '0 6px 16px rgba(204,255,0,0.25)' }}
                whileTap={{ scale: 0.97 }}
                data-testid="personal-soulboard-cta"
              >
                [ {c.psCta} ]
              </motion.button>
            </div>
          </motion.div>

          {/* RIGHT: Botticelli / Three Graces artwork, natural aspect */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img
              src={c.psImage}
              alt="The Personal Soulboard"
              className="block w-full h-auto"
              loading="lazy"
              data-testid="personal-soulboard-image"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PersonalSoulboardSection;
